import { InjectionToken, Type } from '@angular/core';
import { Observable } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ExplorerResourceAction<T = any> {
	label: string;
	onClick?: (resource: T) => void;
	link?: (resource: T) => string[];
	styleClass?: string;
	isVisible?: (resource: T) => boolean;
}

/**
 * Definition for a resource kind in the explorer.
 *
 * Use `defineExplorerResource<YourModel>()` to get typed callbacks:
 * ```ts
 * defineExplorerResource<EntityFile>({
 *   kind: 'file',
 *   getName: (r) => r.publicFilename || 'Unknown', // r is EntityFile
 * })
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ExplorerResourceDefinition<T = any> {
	kind: string;

	/** Load the full resource from its ID. Stores may return null when not found. */
	load: (id: string) => Observable<T | null>;

	/** Display name of the resource. */
	getName: (resource: T) => string;

	/** SVG/emoji icon string. */
	getIcon?: (resource: T) => string;

	/** Angular component used as icon (overrides getIcon). */
	iconComponent?: Type<unknown>;

	/** Route link for clicking the name / "View" button. */
	viewLink?: (resource: T) => string[];

	/** Row-level actions. */
	actions?: ExplorerResourceAction<T>[];

	/** Share action. */
	onShare?: (resource: T) => void;

	// --- Universal column callbacks (Google Drive style) ---

	/** Type badge label (e.g. "Audio", "PDF"). */
	getTypeBadge?: (resource: T) => string | null;

	/** Size in bytes (piped through OctetHumanReadablePipe). */
	getSize?: (resource: T) => number | null;

	/** Formatted date string for the "Modified" column. */
	getDate?: (resource: T) => string | null;

	// --- Preview panel ---

	/** Component rendered in the preview panel. Must accept a `resource` input. */
	previewComponent?: Type<unknown>;

	/** Create action shown in toolbar. */
	createAction?: {
		label: string;
		icon?: string;
		iconComponent?: Type<unknown>;
		onClick: (folderId: string | null) => Observable<unknown> | void;
	};
}

/**
 * Type-safe helper to define an explorer resource definition.
 * Provides autocompletion and compile-time checks for callbacks.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function defineExplorerResource<T>(def: ExplorerResourceDefinition<T>): ExplorerResourceDefinition<any> {
	return def;
}

export interface ExplorerVirtualFolder {
	id: string;
	name: string;
	link: string[];
	iconComponent?: Type<unknown>;
}

export const EXPLORER_ROOT_VIRTUAL_FOLDERS = new InjectionToken<ExplorerVirtualFolder[]>('EXPLORER_ROOT_VIRTUAL_FOLDERS');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const EXPLORER_RESOURCE_DEFINITIONS = new InjectionToken<ExplorerResourceDefinition<any>[]>('EXPLORER_RESOURCE_DEFINITIONS');
